import type { ComponentType } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import FeatureUpgradeBanner from "app/pages/sales-intelligence/common-components/feature-upgrade/components/FeatureUpgradeBanner/FeatureUpgradeBanner";
import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { useSignalAccessRequestSent } from "app/pages/sales-intelligence/modules/signals/hooks/useSignalAccessRequestSent";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";
import { featureHookKeys } from "app/pages/sales-intelligence/utils/i18n/feature-hook";

type Deps = {
    signalType: SignalType;
    onRequestAccess: VoidFunction;
};

/**
 * The upgrade surface a locked signal type shows in place of its picker. Zero-prop by construction:
 * `FilterGateStatus.view` renders it without arguments, so the type is bound here rather than passed.
 *
 * The submitted flag is per type and survives a remount, so the banner does not invite a second request
 * the moment the card is collapsed and reopened.
 */
export const createSignalTypeLockedView = ({ signalType, onRequestAccess }: Deps): ComponentType => {
    const SignalTypeLockedView = () => {
        const [isAccessRequestSent, markAccessRequestSent] = useSignalAccessRequestSent(signalType);

        const handleSubmit = (): void => {
            onRequestAccess();
            markAccessRequestSent();
        };

        return (
            <Flex
                data-automation={`signals-${signalType}-locked-view`}
                height="100%"
                alignItems="center"
                justifyContent="center"
            >
                <FeatureUpgradeBanner
                    isSubmitted={isAccessRequestSent}
                    titleKey={signalsI18nKeys.lockedState.title(signalType)}
                    subtitleKey={signalsI18nKeys.lockedState.subtitle(signalType)}
                    buttonTextKey={featureHookKeys.speakWithUsCta}
                    onSubmit={handleSubmit}
                />
            </Flex>
        );
    };

    SignalTypeLockedView.displayName = `SignalTypeLockedView(${signalType})`;

    return SignalTypeLockedView;
};
